import * as z from 'zod';

// --- Backend-derived enums ---

const companyTypeSchema = z.enum([
  'buy-to-retire',
  'buy-to-sell',
  'journalist',
  'consultant',
  'developer',
  'investor',
  'other',
]);

const compassSubscriptionTypeSchema = z.enum(['none', 'full', 'individual']);

// --- Nested schemas ---

const featureSchema = z.object({
  name: z.string(),
  display_name: z.string(),
  category: z.string(),
});

const compassProjectTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  project_group_id: z.string().uuid(),
  display_name: z.string(),
  // Backend returns null for project types without an assigned glyph (e.g. the
  // Improved Rice Cultivation / Rice methane types). A non-nullable `logo` made
  // `userMeResponseSchema.parse()` throw for any user whose compass_modules
  // included such a type, crashing the whole app (CALYX-PLATFORM-UI-6C).
  logo: z.string().nullable(),
  acronyms: z.array(z.string()),
});

const compassModuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  display_name: z.string(),
  description: z.string().nullable(),
  project_types: z.array(compassProjectTypeSchema),
  has_permission: z.boolean(),
});

// My Plan & VAS Phase 1 — counters + period dates surfaced on /user/me.
// `limit: null` means the benefit is NOT included / no allotment (hide it); it
// is NEVER "unlimited" (the backend rejects debits on a null limit). Truly
// unlimited deep-dive is signalled separately by `deep_dive.has_all_access`
// (plan-permission derived); fastpass/votes have no unlimited tier, so their
// null limit strictly means no entitlement. `benefits: null` for orgs not yet
// provisioned with an `org_plan_allowance` row.
const counterSchema = z.object({
  used: z.number().int().min(0),
  limit: z.number().int().min(0).nullable(),
});

// Phase-1 surfaces fastpass + deep_dive counters; the screener counter
// stays on its own endpoint (PropelAuth metadata) until a counter card lands.
const benefitsSchema = z.object({
  fastpass: counterSchema,
  // `has_all_access`: the org/plan already grants deep-dive on every project, so there
  // is nothing left to redeem and the dashboard tile should be hidden. Optional for
  // backward compatibility with payloads predating the flag.
  deep_dive: counterSchema.extend({ has_all_access: z.boolean().optional() }),
  // Ratings Queue: per-user monthly vote allowance (calendar-month cadence).
  // Optional for backward-compat with payloads predating the feature.
  votes: counterSchema.optional(),
  period_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  period_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

// --- Root /user/me response schema ---

export const userMeResponseSchema = z.object({
  user_id: z.string().uuid(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  features: z.array(featureSchema),
  // Project Screener access moved out of `features` into its own flag.
  screener_access: z.boolean().optional(),
  plan: z.string().nullable(),
  plan_subscription_type: z.string().nullable(),
  is_demo: z.boolean(),
  is_plan_public: z.boolean(),
  compass: z.boolean(),
  compass_subscription_type: compassSubscriptionTypeSchema,
  last_verified_at: z.string().nullable(),
  latest_ui_version: z.string().nullable(),
  company_type: companyTypeSchema.nullable(),
  compass_modules: z.array(compassModuleSchema).default([]),
  resent_count: z.number().optional(),
  show_super_pollutants_terms_modal: z.boolean().optional(),
  // HMAC-SHA256 hash used to verify the user's identity to ClearFeed. Signed on the
  // backend; passed to clearfeed.init({ user.hash }) for identity verification.
  clearfeed_user_hash: z.string().nullable().optional(),
  // My Plan & VAS — counters + period dates (Phase 1).
  benefits: benefitsSchema.nullable().optional(),
  // Org subscription window (PropelAuth) — drives the My Plan header Start/Renewal
  // dates independent of `benefits` (which is null until an AM configures the
  // allowance). Optional for payloads predating the field; dates null if unset.
  subscription: z
    .object({ start_date: z.string().nullable(), end_date: z.string().nullable() })
    .optional(),
  // Legacy field — kept for backward compatibility
  freemium_experience: z.boolean().optional(),
});

// --- Inferred TypeScript types ---

export type UserMeResponse = z.infer<typeof userMeResponseSchema>;
export type Feature = z.infer<typeof featureSchema>;
export type CompassModule = z.infer<typeof compassModuleSchema>;
export type CompassProjectType = z.infer<typeof compassProjectTypeSchema>;
export type CompanyType = z.infer<typeof companyTypeSchema>;
export type CompassSubscriptionType = z.infer<typeof compassSubscriptionTypeSchema>;
export type BenefitCounter = z.infer<typeof counterSchema>;
